import { PaymentTypeEnum } from "@frontend/lib/graphql/generated-gql";
// import type { LanguageCode } from "@frontend/lib/graphql/generated-gql";
import type { GetBoughtTreatmentsByIdsQueryBoughtTreatment as BoughtTreatment } from "@frontend/lib/graphql/gql-types";
import type { TypedTranslationFunction } from "@frontend/lib/next-intl-typed";
import { format } from "date-fns";
import type { ReactNode } from "react";

export enum PRICING_KEY {
  COPAY = "copay",
  COINSURANCE = "coinsurance",
  PRE_DEDUCTIBLE = "deductible",
  HIT_OOP_MAX = "out of pocket max",
  GLOBAL_BILLING = "global billing",
  CONTRACT = "contract",
  ALLOWABLE = "allowable",
}

export type KioskPricedTreatment = {
  id: number;
  requestId: string | null;
  copayUsed: boolean;
  metadata: Record<string, unknown>;
  hrtId: number;
  price: number;
  paymentType: "INSURED" | "SELFPAY";
  pricingEntityIds: number[];
};

// modify this when we get more metadata
export type PricingMetadata = {
  copayUsed: boolean;
  coinsUsed: boolean;
  deductibleUsed: boolean;
  oopMaxUsed: boolean;
  globalBillingUsed: boolean;
  contractUsed: boolean;
  coinsurancePercent?: number;
  pnr?: number; // need to truncate this at 2 decimal places
  contractName?: string;
  globalPeriodDateOfService?: string;
  globalPeriodProcedureName?: string;
  remainingDeductiblePreTreatment?: number;
  remainingOopMaxPreTreatment?: number;
  planTotalDeductible?: number;
  planTotalOopMax?: number;
  insuredFailedSelfPayUsed?: boolean;
  serviceType?: string; // used for copay type mainly
};

export const getPricingExplanation = (
  metadata: PricingMetadata,
  boughtTreatment: BoughtTreatment | null,
  keys: PRICING_KEY[],
  zeroDollar: boolean,
  t: TypedTranslationFunction<"PricingExplanation">,
) => {
  // check bt first for any overrides. SELFPAYOVERRIDE / INSURANCEOVERRIDE are
  // the two MANUAL overrides (a practice-set price, not benefit math) — they
  // share one explanation, distinct from plain SELFPAY (never staff-touched).
  if (boughtTreatment !== null) {
    if (boughtTreatment.paymentType === PaymentTypeEnum.Selfpay) {
      return t("SelfPay");
    }
    if (
      boughtTreatment.paymentType === PaymentTypeEnum.Selfpayoverride ||
      boughtTreatment.paymentType === PaymentTypeEnum.Insuranceoverride
    ) {
      return t("PriceManuallyEstimated");
    }
    if (boughtTreatment.paymentType === PaymentTypeEnum.Insurancemakeitfree) {
      return t("TreatmentFree");
    }
    if (boughtTreatment.paymentType === PaymentTypeEnum.Postclaim) {
      return t("PostClaim");
    }
  }

  if (keys.length <= 1) {
    const key = keys[0];
    switch (key) {
      case PRICING_KEY.COPAY: {
        return t("Copay", {
          copayType: metadata.serviceType || "",
        });
      }
      case PRICING_KEY.COINSURANCE: {
        return t("Coinsurance", {
          coinsurancePercentage: metadata.coinsurancePercent,
        });
      }
      case PRICING_KEY.PRE_DEDUCTIBLE: {
        return t("PreDeductible");
      }
      case PRICING_KEY.HIT_OOP_MAX: {
        return t("OutofPocketMax");
      }
      case PRICING_KEY.GLOBAL_BILLING: {
        return t("GlobalBilling", {
          globalBillingTreatmentName: metadata.globalPeriodProcedureName,
          globalBillingTreatmentDate: format(
            metadata.globalPeriodDateOfService ?? new Date(),
            "MM/dd/yyyy",
          ),
        });
      }
      case PRICING_KEY.CONTRACT: {
        return t("Contract", {
          contractName: metadata.contractName,
        });
      }
      case undefined: {
        if (metadata?.insuredFailedSelfPayUsed) {
          return t("OutOfNetwork");
        }
        // must be active
        if (zeroDollar) {
          return t("UnknownZeroDollar");
        }
        return t("UnknownNonZeroDollar");
      }
    }
  }
  if (keys.length === 2) {
    if (
      keys.includes(PRICING_KEY.PRE_DEDUCTIBLE) &&
      keys.includes(PRICING_KEY.COINSURANCE)
    ) {
      return t("PreDeductibleCoinsurance", {
        coinsurancePercentage: metadata.coinsurancePercent,
      });
    }
    if (
      keys.includes(PRICING_KEY.HIT_OOP_MAX) &&
      keys.includes(PRICING_KEY.COINSURANCE)
    ) {
      return t("OutofPocketMaxCoinsurance");
    }
    if (
      keys.includes(PRICING_KEY.COPAY) &&
      keys.includes(PRICING_KEY.COINSURANCE)
    ) {
      return t("CopayCoinsurance");
    }
  }
};

const getRegexForLocale = (userLocale: string) => {
  return {
    en: /(copay|coinsurance|deductible|out of pocket max|global billing|contract|allowable)/gi,
    es: /(copago|coseguro|deducible|máximo de gastos de bolsillo|facturación global|contrato|permitido)/gi,
    ru: /(доплата|сострахование|франшиза|максимум из кармана|периода глобального выставления|контракт|разрешенный)/gi,
    zh: /(共付额|共同保险|免赔额|自付费用上限|全球计费|合同|允许)/gi,
    zh_HK: /(共付額|共同保險|免賠額|自付費用上限|全球計費|合約|允許)/gi,
  }[userLocale];
};

export const convertTranslatedWordToKey = (word: string) => {
  const copayRegex = /(copay|copago|доплата|доплаты|共付额|共付額)/gi;
  const coinsuranceRegex =
    /(coinsurance|coseguro|сострахование|共同保险|共同保險)/gi;
  const deductibleRegex =
    /(deductible|deducible|франшиза|франшизу|免赔额|免賠額)/gi;
  const oopMaxRegex =
    /(out of pocket max|máximo de gastos de bolsillo|максимум из кармана|自付费用上限|自付費用上限)/gi;
  const globalBillingRegex =
    /(global billing|facturación global|глобальное выставление счетов|периода глобального выставления|全球计费|全球計費)/gi;
  const contractRegex = /(contract|contrato|контракту|контракт|合同|合約)/gi;
  const allowableRegex = /(allowable|permitido|разрешенный|允许|允許)/gi;
  if (copayRegex.test(word)) {
    return PRICING_KEY.COPAY;
  }
  if (coinsuranceRegex.test(word)) {
    return PRICING_KEY.COINSURANCE;
  }
  if (deductibleRegex.test(word)) {
    return PRICING_KEY.PRE_DEDUCTIBLE;
  }
  if (oopMaxRegex.test(word)) {
    return PRICING_KEY.HIT_OOP_MAX;
  }
  if (globalBillingRegex.test(word)) {
    return PRICING_KEY.GLOBAL_BILLING;
  }
  if (contractRegex.test(word)) {
    return PRICING_KEY.CONTRACT;
  }
  if (allowableRegex.test(word)) {
    return PRICING_KEY.ALLOWABLE;
  }
  return undefined;
};

export const replacePricingWordsWithLinks = (
  explanation: string,
  onClick: (word: string) => void,
  highlightColor?: string,
  locale = "en",
): ReactNode[] => {
  const regex = getRegexForLocale(locale);
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  if (!regex) {
    return [
      <p className="text-sm text-gray-600" key="explanation">
        {explanation}
      </p>,
    ];
  }

  // Reset regex lastIndex to ensure we start from the beginning
  regex.lastIndex = 0;

  const max_iterations = 5; // don't think we'll ever have more than 5 matches to replace
  let iterations = 0;
  while (iterations < max_iterations) {
    match = regex.exec(explanation);
    if (match === null) break;
    iterations++;
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(explanation.slice(lastIndex, match.index));
    }

    // Add the clickable span for the match
    const matchedWord = match[0];
    parts.push(
      <button
        key={`${matchedWord}-${match.index}`}
        type="button"
        data-testid={`pricing-word-${matchedWord.replace(/ /g, "-").toLowerCase()}`}
        className="font-medium italic no-underline cursor-pointer bg-transparent border-none p-0"
        style={{ color: highlightColor || "#00a3ff" }}
        onClick={() => onClick(matchedWord)}
      >
        {matchedWord}
      </button>,
    );

    lastIndex = regex.lastIndex;
  }

  // Add remaining text after the last match
  if (lastIndex < explanation.length) {
    parts.push(explanation.slice(lastIndex));
  }

  return parts;
};

export const getPricingKeysFromMetadata = (metadata: PricingMetadata) => {
  const keys: PRICING_KEY[] = [];
  if (metadata?.copayUsed) {
    keys.push(PRICING_KEY.COPAY);
  }
  if (metadata?.coinsUsed) {
    keys.push(PRICING_KEY.COINSURANCE);
  }
  if (metadata?.deductibleUsed) {
    keys.push(PRICING_KEY.PRE_DEDUCTIBLE);
  }
  if (metadata?.oopMaxUsed) {
    keys.push(PRICING_KEY.HIT_OOP_MAX);
  }
  if (metadata?.globalBillingUsed) {
    keys.push(PRICING_KEY.GLOBAL_BILLING);
  }
  if (metadata?.contractUsed) {
    keys.push(PRICING_KEY.CONTRACT);
  }
  return keys;
};
